import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { GetMyProfileResponse, UpdateMyProfileRequest } from '../../types/auth.js'

import { apiClient } from '../../lib/client.js'
import { meKeys } from './keys.js'

export const useUpdateMyProfileMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateMyProfileRequest) => {
      const response = await apiClient.client.put<GetMyProfileResponse>('/me/profile', data)
      return response.data
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: meKeys.profile })
      void queryClient.invalidateQueries({ queryKey: meKeys.me })
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: meKeys.profile })

      const previousProfile = queryClient.getQueryData<GetMyProfileResponse>(meKeys.profile)

      if (previousProfile) {
        const isCompany = previousProfile.client?.clientType === 'COMPANY'
        // Pour un B2B, le nom d'affichage (User.firstName) suit la raison sociale.
        const displayFirstName = isCompany
          ? (data.companyName ?? previousProfile.user.firstName)
          : (data.firstName ?? previousProfile.user.firstName)

        queryClient.setQueryData<GetMyProfileResponse>(meKeys.profile, {
          ...previousProfile,
          client: previousProfile.client
            ? {
                ...previousProfile.client,
                companyName: data.companyName ?? previousProfile.client.companyName,
                address: data.address ?? previousProfile.client.address,
                birthDate: data.birthDate ?? previousProfile.client.birthDate,
                city: data.city ?? previousProfile.client.city,
                gender: data.gender ?? previousProfile.client.gender,
                postalCode: data.postalCode ?? previousProfile.client.postalCode,
              }
            : null,
          user: {
            ...previousProfile.user,
            firstName: displayFirstName,
            lastName: isCompany ? '' : (data.lastName ?? previousProfile.user.lastName),
          },
        })
      }

      return { previousProfile }
    },
  })
}
